// Silent Dispatcher Mini App — Phase 1 Plan 05.
//
// Replaces Plan 00 walking-skeleton stub with full Walking Skeleton flow:
//   / → ConsentScreen → /register → RegistrationScreen → /trial → TrialActivatedScreen
//
// Provider order (outer → inner):
//   AuthProvider (depends on initDataRaw passed from main.tsx)
//   QueryClientProvider (react-query for ConsentScreen legal fetch — optional Phase 5)
//   BrowserRouter (react-router-dom v7)

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthProvider } from "./auth/AuthContext";
import { TabbarLayout } from "./components/Tabbar";
import { Carriers } from "./screens/Carriers";
import { ConfirmDelete } from "./screens/ConfirmDelete";
import { ConsentScreen } from "./screens/ConsentScreen";
import { Dashboard } from "./screens/Dashboard";
import { Drivers } from "./screens/Drivers";
import { ExcelImport } from "./screens/ExcelImport";
import { InviteDriverScreen } from "./screens/InviteDriverScreen";
import { Members } from "./screens/Members";
import { NewTrip } from "./screens/NewTrip";
import { RegistrationScreen } from "./screens/RegistrationScreen";
import { Reports } from "./screens/Reports";
import { SccClickwrap } from "./screens/SccClickwrap";
import { Settings } from "./screens/Settings";
import { SettingsLegal } from "./screens/SettingsLegal";
import { TrialActivatedScreen } from "./screens/TrialActivatedScreen";
import { TripDetails } from "./screens/TripDetails";
import { TripsList } from "./screens/TripsList";

export interface AppProps {
  baseUrl: string;
  initDataRaw: string | null;
}

export function App({ baseUrl, initDataRaw }: AppProps): React.JSX.Element {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, staleTime: 60_000 },
        },
      }),
    [],
  );

  return (
    <AuthProvider baseUrl={baseUrl} initDataRaw={initDataRaw}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Bot WebApp button opens MINIAPP_URL=…/app (.env.example
                canonical) — the SPA routes live at /. Without this redirect
                Telegram users land on an empty <Routes> render (blank screen,
                bug 2026-06-12). */}
            <Route path="/app" element={<Navigate to="/" replace />} />
            {/* Phase 5 Plan 05-03 — GDPR confirm landing (token-bound, no auth) */}
            <Route path="/gdpr/confirm-delete" element={<ConfirmDelete baseUrl={baseUrl} />} />
            <Route path="/" element={<ConsentScreen baseUrl={baseUrl} />} />
            <Route path="/register" element={<RegistrationScreen />} />
            <Route path="/trial" element={<TrialActivatedScreen />} />
            {/* Phase 2 Plan 02-05 — DRV-01 driver invite surface */}
            <Route path="/dispatcher/invite-driver" element={<InviteDriverScreen />} />
            {/* Phase 4 Plan 04-01 — DIS-04 dispatcher members + invites */}
            <Route
              path="/dispatcher/members"
              element={
                <TabbarLayout>
                  <Members />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-02 — DIS-08..13 Excel import 4-step wizard */}
            <Route
              path="/dispatcher/trips/import"
              element={
                <TabbarLayout>
                  <ExcelImport />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-03 — DIS-07 NewTrip multi-stop form (D-43, D-75) */}
            <Route
              path="/dispatcher/trips/new"
              element={
                <TabbarLayout>
                  <NewTrip />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-07 — DIS-18 TripDetails (4 tabs) */}
            <Route
              path="/dispatcher/trips/:tripId"
              element={
                <TabbarLayout>
                  <TripDetails />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-07 — DIS-16 TripsList (3 tabs + infinite scroll) */}
            <Route
              path="/dispatcher/trips"
              element={
                <TabbarLayout>
                  <TripsList />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-03 — DIS-05 Drivers list with status pills */}
            <Route
              path="/dispatcher/drivers"
              element={
                <TabbarLayout>
                  <Drivers />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-04 — FWD-04 Carriers CRM (owner-only via D-70) */}
            <Route
              path="/dispatcher/carriers"
              element={
                <TabbarLayout>
                  <Carriers />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-05 — DIS-14 Dashboard (live map + alerts) */}
            <Route
              path="/dispatcher/dashboard"
              element={
                <TabbarLayout>
                  <Dashboard
                    baseUrl={baseUrl}
                    hereApiKey={
                      (
                        import.meta as unknown as {
                          env?: Record<string, string | undefined>;
                        }
                      ).env?.VITE_HERE_API_KEY
                    }
                  />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-06 — DIS-19/21 Reports (xlsx + pdf) */}
            <Route
              path="/dispatcher/reports"
              element={
                <TabbarLayout>
                  <Reports baseUrl={baseUrl} />
                </TabbarLayout>
              }
            />
            {/* Phase 4 Plan 04-07 — DIS-20 Settings (language + notif + owner vis) */}
            <Route
              path="/dispatcher/settings"
              element={
                <TabbarLayout>
                  <Settings />
                </TabbarLayout>
              }
            />
            {/* Phase 5 Plan 05-04 — SCC Module 2 clickwrap (TR/RS, D-88).
                No TabbarLayout — modal-style screen interrupts onboarding flow
                until SCC accepted. ISSUE-07 fix: inline markdown viewer, no PDF embed. */}
            <Route path="/scc-clickwrap" element={<SccClickwrap baseUrl={baseUrl} />} />
            {/* Phase 5 Plan 05-04 — Owner-only legal docs download (D-89).
                7 doc_types (driver/dispatcher/customer PP v2.0 + DPIA/DPA/SCC/LIA).
                PDF downloads open in external browser via Telegram.WebApp.openLink. */}
            <Route
              path="/settings/legal"
              element={
                <TabbarLayout>
                  <SettingsLegal baseUrl={baseUrl} />
                </TabbarLayout>
              }
            />
            {/* Catch-all — unknown paths fall back to consent flow instead of
                a blank screen (defense-in-depth for future path drift). */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  );
}
